import React from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';

import useResponsive from '~/hooks/useResponsive';

import DialogView from './components/DialogView';
import SideDrawerContent from './components/SideDrawerContent';
import SideDrawerDescription from './components/SideDrawerDescription';
import SideDrawerFooter from './components/SideDrawerFooter';
import SideDrawerHeader from './components/SideDrawerHeader';

export type PrimaryColorTag =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';

export interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  onCancel?: () => void;
  title: React.ReactNode;
  description?: string;
  anchor?: 'left' | 'right' | 'bottom';
  children?: React.ReactNode;
  width?: number | string;
  isSideDrawer?: boolean;
  onSave?: () => void;
  isLoading?: boolean;
  isError?: boolean;
  buttonLabel?: string;
  loaderRows?: number;
  learnMoreHref?: string;
  learningMoreText?: string;
  primaryDisabled?: boolean;
  primaryTestId?: string;
  icon?: React.ReactNode;
  primaryActionColor?: PrimaryColorTag;
  sideDrawerTestId?: string;
  cancelTestId?: string;
  headerActions?: React.ReactNode;
  isContainer?: boolean;
  disableMobilePadding?: boolean;
  isFullWidthContent?: boolean;
  isCancelButtonPrimary?: boolean;
  id?: string;
}

const DEFAULT_WIDTH = 640;
const BORDER_RADIUS = 12;
const DEFAULT_LOADER_ROWS = 3;

const SideDrawer: React.FC<SideDrawerProps> = ({
  open,
  onClose,
  onCancel = undefined,
  title,
  description = '',
  anchor = 'right',
  children = null,
  width = DEFAULT_WIDTH,
  isSideDrawer = true,
  onSave = undefined,
  isLoading = false,
  isError = false,
  buttonLabel = '',
  loaderRows = DEFAULT_LOADER_ROWS,
  learnMoreHref = '',
  learningMoreText = '',
  primaryDisabled = false,
  primaryTestId = '',
  icon = null,
  primaryActionColor = 'primary',
  sideDrawerTestId = 'side-drawer',
  cancelTestId = 'cancel-button',
  headerActions = null,
  isContainer = false,
  disableMobilePadding = false,
  isFullWidthContent = false,
  isCancelButtonPrimary = false,
  id = undefined,
  ...props
}) => {
  const { isMobile } = useResponsive();
  const isMobileView = isMobile();
  const { t } = useTranslation('common');
  const drawerAnchor = isMobileView ? 'bottom' : anchor;
  const drawerWidth = isMobileView ? '100% !important' : width;
  const handleDrawerClose = (_: unknown, reason: string) => {
    if (reason !== 'backdropClick') {
      onClose();
    }
  };
  const footerPrimaryAction = onSave
    ? {
        label: buttonLabel || t('common:actions.continue'),
        onClick: onSave,
        loading: isLoading,
        disabled: primaryDisabled,
        dataTestId: primaryTestId,
        color: primaryActionColor,
      }
    : undefined;

  // Show dialog instead of drawer on desktop when isSideDrawer is false
  if (!isSideDrawer && !isMobileView) {
    return (
      <DialogView
        open={open}
        onClose={onClose}
        onSave={onSave}
        title={title}
        isError={isError}
        isLoading={isLoading}
        buttonLabel={buttonLabel}
        primaryDisabled={primaryDisabled}
        primaryTestId={primaryTestId}
        primaryActionColor={primaryActionColor}
        sideDrawerTestId={sideDrawerTestId}
        cancelTestId={cancelTestId}
        loaderRows={loaderRows}
        headerActions={headerActions}
        isCancelButtonPrimary={isCancelButtonPrimary}
        {...props}
      >
        {children}
      </DialogView>
    );
  }

  return (
    <Drawer
      anchor={drawerAnchor}
      open={open}
      onClose={handleDrawerClose}
      data-testid={sideDrawerTestId}
      ModalProps={{ keepMounted: true }}
      PaperProps={id ? { id } : undefined}
      sx={{
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          maxWidth: '100%',
          height: isMobileView ? 'auto' : '100%',
          display: 'flex',
          flexDirection: 'column',
          borderTopLeftRadius: BORDER_RADIUS,
          borderTopRightRadius: isMobileView ? BORDER_RADIUS : 0,
          borderBottomLeftRadius: isMobileView ? 0 : BORDER_RADIUS,
          overflow: 'hidden',
        },
      }}
      {...props}
    >
      {isContainer ? (
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      ) : (
        <>
          {/* Main Content */}
          <Box
            sx={{
              flex: isMobileView ? 'unset' : 1,
              overflowY: 'auto',
              p: 4,
              px: isMobileView && disableMobilePadding ? 0 : 4,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack spacing={1}>
              <SideDrawerHeader
                title={title}
                onClose={onClose}
                icon={icon}
                isMobileView={isMobileView}
                showDivider={!isSideDrawer}
                actions={headerActions}
              />
              {description && (
                <SideDrawerDescription
                  description={description}
                  learnMoreText={learningMoreText}
                  learnMoreHref={learnMoreHref}
                  isMobileView={isMobileView}
                />
              )}
            </Stack>

            <SideDrawerContent
              isError={isError}
              loaderRows={loaderRows}
              fullWidth={isFullWidthContent}
            >
              {children}
            </SideDrawerContent>
          </Box>

          {/* Footer */}
          <SideDrawerFooter
            onCancel={onCancel || onClose}
            cancelTestId={cancelTestId}
            isMobileView={isMobileView}
            footerPrimaryAction={footerPrimaryAction}
            isCancelButtonPrimary={isCancelButtonPrimary}
          />
        </>
      )}
    </Drawer>
  );
};

export default SideDrawer;
